/**
 * CompliancePanel - Unified Compliance Review Dashboard
 *
 * Single-page view showing:
 * - Identity verification status
 * - Sanctions, PEP, and Adverse Media screening
 * - Screening hits with AI-powered false positive analysis
 * - Risk score and recommended action
 */

import React, { useState, useMemo } from 'react';
import {
  Shield,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  User,
  FileText,
  Bot,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  ExternalLink,
  Newspaper,
} from 'lucide-react';
import { useComplianceSummary } from '../hooks/useApplicants';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { ScreeningService } from '../services';
import { useToast } from '../contexts/ToastContext';
import { formatListSource } from '../utils/formatters';
import { Button, Badge, Card } from './ui';
import { cn } from '../lib/utils';

// Status card configurations
const statusConfig = {
  verified: { color: 'success', icon: CheckCircle2, label: 'Verified' },
  clear: { color: 'success', icon: CheckCircle2, label: 'Clear' },
  pending: { color: 'warning', icon: AlertTriangle, label: 'Pending' },
  not_run: { color: 'muted', icon: Shield, label: 'Not Run' },
  hit: { color: 'danger', icon: XCircle, label: 'Hit' },
  failed: { color: 'danger', icon: XCircle, label: 'Failed' },
};

const hitTypeLabels = {
  sanctions: 'Sanctions',
  pep: 'PEP',
  adverse_media: 'Adverse Media',
};

const resolutionLabels = {
  pending: 'Pending Review',
  confirmed_true: 'Confirmed Match',
  confirmed_false: 'Cleared',
};

// Status Card Component
function StatusCard({ title, status, subtitle, icon: Icon, onClick, isActive }) {
  const config = statusConfig[status] || statusConfig.pending;
  const StatusIcon = config.icon;

  const colorClasses = {
    success: 'text-green-500',
    warning: 'text-orange-500',
    danger: 'text-red-500',
    muted: 'text-grey-400',
  };

  return (
    <Card
      className={cn(
        'p-4 transition-all',
        onClick && 'cursor-pointer hover:-translate-y-0.5 hover:shadow-lg',
        isActive && 'border-teal-500 ring-2 ring-teal-500/20'
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 mb-2 text-grey-400 text-xs uppercase tracking-wide">
        {Icon && <Icon size={16} />}
        <span>{title}</span>
      </div>
      <div className={cn('flex items-center gap-2', colorClasses[config.color])}>
        <StatusIcon size={20} />
        <span className="font-semibold text-sm">{config.label}</span>
      </div>
      {subtitle && (
        <div className="mt-1 text-xs text-grey-400">{subtitle}</div>
      )}
    </Card>
  );
}

// Risk Score Bar Component
function RiskScoreBar({ score, level }) {
  const getColor = () => {
    if (score >= 60) return 'danger';
    if (score >= 40) return 'warning';
    return 'success';
  };

  const colorClasses = {
    success: 'text-green-500 bg-green-500',
    warning: 'text-orange-500 bg-orange-500',
    danger: 'text-red-500 bg-red-500',
  };

  return (
    <Card className="p-4 mb-4">
      <div className="flex justify-between items-center mb-2 text-sm">
        <span>Overall Risk Score</span>
        <span className={cn('font-semibold', colorClasses[getColor()].split(' ')[0])}>
          {score ?? '—'}/100 ({level?.toUpperCase() || 'UNKNOWN'})
        </span>
      </div>
      <div className="h-2 bg-grey-100 dark:bg-grey-800 rounded overflow-hidden">
        <div
          className={cn('h-full rounded transition-all duration-300', colorClasses[getColor()].split(' ')[1])}
          style={{ width: `${score ?? 0}%` }}
        />
      </div>
    </Card>
  );
}

// AI Analysis Badge Component
function AIAnalysisBadge({ analysis }) {
  if (!analysis) return null;

  const isLikelyFalsePositive = analysis.suggested_resolution === 'confirmed_false';
  const confidencePercent = Math.round(analysis.confidence * 100);

  return (
    <Badge variant={isLikelyFalsePositive ? 'success' : 'warning'} className="mb-2">
      <Bot size={14} />
      AI: Likely {isLikelyFalsePositive ? 'FALSE POSITIVE' : 'TRUE MATCH'} ({confidencePercent}%)
    </Badge>
  );
}

// Hit Card Component
function HitCard({ hit, onResolve, isResolving }) {
  const [expanded, setExpanded] = useState(false);

  const handleClear = () => {
    onResolve(hit.id, 'confirmed_false');
  };

  const handleConfirm = () => {
    onResolve(hit.id, 'confirmed_true');
  };

  const typeLabel = hitTypeLabels[hit.hit_type] || hit.hit_type;
  const isPending = hit.resolution_status === 'pending';

  const typeColors = {
    sanctions: 'border-l-red-500',
    pep: 'border-l-orange-500',
    adverse_media: 'border-l-blue-500',
  };

  const typeBadgeVariants = {
    sanctions: 'destructive',
    pep: 'warning',
    adverse_media: 'info',
  };

  return (
    <Card className={cn('p-4 mb-3 border-l-4', typeColors[hit.hit_type] || 'border-l-grey-300')}>
      <div className="flex justify-between items-center cursor-pointer mb-2" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          <Badge variant={typeBadgeVariants[hit.hit_type] || 'secondary'} size="sm">
            {typeLabel.toUpperCase()}
          </Badge>
          <span className="text-[13px] text-grey-400">{formatListSource(hit.source || hit.list_source)}</span>
          {hit.pep_tier && (
            <span className="text-[11px] bg-grey-100 dark:bg-grey-800 px-1.5 py-0.5 rounded">
              Tier {hit.pep_tier}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-grey-400">
          <span className="text-[13px]">{Math.round(hit.confidence)}% confidence</span>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap text-[13px] mb-3">
        <span className="text-grey-400">Matched:</span>
        <span className="font-medium">"{hit.matched_name}"</span>
        <span className="text-grey-400">vs</span>
        <span className="font-medium">"{hit.applicant_name}"</span>
      </div>

      {hit.ai_analysis && (
        <div className="bg-grey-50 dark:bg-grey-800 rounded-md p-3 mb-3">
          <AIAnalysisBadge analysis={hit.ai_analysis} />
          <p className="text-[13px] text-grey-600 dark:text-grey-400 leading-snug m-0">
            {hit.ai_analysis.reasoning}
          </p>

          {expanded && hit.ai_analysis.evidence?.length > 0 && (
            <div className="mt-3 text-xs">
              <strong>Evidence:</strong>
              <ul className="mt-2 pl-4 m-0">
                {hit.ai_analysis.evidence.map((e, idx) => (
                  <li key={idx} className="mb-1">
                    <span className="text-grey-400 capitalize">{e.factor.replace(/_/g, ' ')}:</span> {e.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Adverse Media Details */}
      {hit.hit_type === 'adverse_media' && (hit.article_title || hit.categories?.length > 0) && (
        <div className="bg-grey-50 dark:bg-grey-800 rounded-md p-3 mb-3">
          {hit.article_title && (
            <div className="flex items-start gap-2 flex-wrap">
              <Newspaper size={14} className="flex-shrink-0 mt-0.5 text-blue-500" />
              {hit.article_url ? (
                <a
                  href={hit.article_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-500 no-underline text-[13px] font-medium hover:underline"
                >
                  {hit.article_title}
                  <ExternalLink size={12} />
                </a>
              ) : (
                <span className="text-[13px]">{hit.article_title}</span>
              )}
              {hit.article_date && (
                <span className="text-xs text-grey-400 ml-auto">
                  {new Date(hit.article_date).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
          {hit.categories?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {hit.categories.map((category, idx) => (
                <span key={idx} className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 capitalize">
                  {category.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* PEP Position */}
      {hit.hit_type === 'pep' && hit.pep_position && (
        <div className="text-[13px] mb-3 py-2 px-3 bg-orange-50 dark:bg-orange-950/20 rounded-md flex gap-2">
          <span className="text-grey-400">Position:</span>
          <span>{hit.pep_position}</span>
        </div>
      )}

      {expanded && (
        <div className="mt-3 pt-3 border-t border-grey-200 dark:border-grey-700 text-[13px]">
          {hit.matched_country && (
            <div className="flex gap-2 mb-1">
              <span className="text-grey-400 min-w-[80px]">Country:</span> {hit.matched_country}
            </div>
          )}
          <div className="flex gap-2 mb-1">
            <span className="text-grey-400 min-w-[80px]">Status:</span> {resolutionLabels[hit.resolution_status] || hit.resolution_status}
          </div>
          <div className="flex gap-2">
            <span className="text-grey-400 min-w-[80px]">Created:</span> {new Date(hit.created_at).toLocaleDateString()}
          </div>
        </div>
      )}

      {isPending && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-grey-200 dark:border-grey-700">
          <Button
            variant="success"
            size="sm"
            onClick={handleClear}
            disabled={isResolving}
          >
            {isResolving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Mark as Clear
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleConfirm}
            disabled={isResolving}
          >
            {isResolving ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
            Confirm Match
          </Button>
        </div>
      )}

      {!isPending && (
        <div className={cn(
          'flex items-center gap-1.5 mt-3 pt-3 border-t border-grey-200 dark:border-grey-700 text-[13px]',
          hit.resolution_status === 'confirmed_false' ? 'text-green-500' : 'text-red-500'
        )}>
          {hit.resolution_status === 'confirmed_false' ? (
            <>
              <CheckCircle2 size={14} />
              <span>Cleared as false positive</span>
            </>
          ) : (
            <>
              <XCircle size={14} />
              <span>Confirmed as true match</span>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// Main CompliancePanel Component
export default function CompliancePanel({ applicantId }) {
  const { data: compliance, isLoading, error, refetch } = useComplianceSummary(applicantId);
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [resolvingHit, setResolvingHit] = useState(null);
  const [hitTypeFilter, setHitTypeFilter] = useState('all');

  // Mutation for resolving hits
  const resolveHitMutation = useMutation({
    mutationFn: async ({ hitId, resolution }) => {
      const service = new ScreeningService(getToken);
      return service.resolveHit(hitId, resolution);
    },
    onSuccess: () => {
      // Invalidate compliance summary to refetch
      queryClient.invalidateQueries(['applicants', 'detail', applicantId, 'compliance']);
      toast.success('Hit resolved successfully');
      setResolvingHit(null);
    },
    onError: (error) => {
      toast.error(`Failed to resolve hit: ${error.message}`);
      setResolvingHit(null);
    },
  });

  const handleResolveHit = (hitId, resolution) => {
    setResolvingHit(hitId);
    resolveHitMutation.mutate({ hitId, resolution });
  };

  // Filter hits by type when a status card is clicked (must be before early returns)
  const filteredHits = useMemo(() => {
    const hits = compliance?.hits || [];
    if (hitTypeFilter === 'all') return hits;
    return hits.filter(h => h.hit_type === hitTypeFilter);
  }, [compliance?.hits, hitTypeFilter]);

  const toggleFilter = (filterType) => {
    setHitTypeFilter(hitTypeFilter === filterType ? 'all' : filterType);
  };

  if (isLoading) {
    return (
      <Card className="p-10 flex flex-col items-center justify-center gap-3 text-grey-400">
        <Loader2 size={24} className="animate-spin" />
        <span>Loading compliance data...</span>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-10 flex flex-col items-center justify-center gap-3 text-grey-400">
        <AlertTriangle size={24} />
        <span>Failed to load compliance data</span>
        <Button variant="secondary" size="sm" onClick={() => refetch()}>
          <RefreshCw size={14} />
          Retry
        </Button>
      </Card>
    );
  }

  if (!compliance) {
    return null;
  }

  const { identity, screening, risk_score, risk_level, recommended_action, pending_hits, total_hits } = compliance;

  const screeningSubtitle = (category) => {
    if (category.status === 'not_run') return 'Not yet screened';
    if (category.last_checked_at) {
      const d = new Date(category.last_checked_at);
      return `Checked ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
    return null;
  };

  return (
    <div className="p-5 bg-white dark:bg-grey-900 rounded-xl">
      {/* Status Cards */}
      <div className="grid grid-cols-4 gap-3 mb-5 md:grid-cols-2">
        <StatusCard
          title="Identity"
          status={identity.status}
          subtitle={identity.face_match_score ? `Face: ${Math.round(identity.face_match_score)}%` : null}
          icon={User}
        />
        <StatusCard
          title="Sanctions"
          status={screening.sanctions.status}
          subtitle={screening.sanctions.hit_count > 0 ? `${screening.sanctions.hit_count} hit(s)` : screeningSubtitle(screening.sanctions)}
          icon={Shield}
          onClick={screening.sanctions.hit_count > 0 ? () => toggleFilter('sanctions') : undefined}
          isActive={hitTypeFilter === 'sanctions'}
        />
        <StatusCard
          title="PEP"
          status={screening.pep.status}
          subtitle={screening.pep.hit_count > 0 ? `${screening.pep.hit_count} hit(s)` : screeningSubtitle(screening.pep)}
          icon={Shield}
          onClick={screening.pep.hit_count > 0 ? () => toggleFilter('pep') : undefined}
          isActive={hitTypeFilter === 'pep'}
        />
        <StatusCard
          title="Adverse Media"
          status={screening.adverse_media.status}
          subtitle={screening.adverse_media.hit_count > 0 ? `${screening.adverse_media.hit_count} hit(s)` : screeningSubtitle(screening.adverse_media)}
          icon={FileText}
          onClick={screening.adverse_media.hit_count > 0 ? () => toggleFilter('adverse_media') : undefined}
          isActive={hitTypeFilter === 'adverse_media'}
        />
      </div>

      {/* Risk Score */}
      <RiskScoreBar score={risk_score} level={risk_level} />

      {/* Recommended Action */}
      <Card className="p-3 mb-5 flex items-center gap-2">
        <span className="text-grey-400 text-sm">Recommended Action:</span>
        <span className={cn(
          'font-semibold text-sm',
          recommended_action === 'auto_approve' && 'text-green-500',
          recommended_action === 'manual_review' && 'text-orange-500',
          recommended_action === 'auto_reject' && 'text-red-500'
        )}>
          {recommended_action === 'auto_approve' && 'Auto Approve'}
          {recommended_action === 'manual_review' && 'Manual Review Required'}
          {recommended_action === 'auto_reject' && 'Auto Reject'}
        </span>
      </Card>

      {/* Screening Hits */}
      {total_hits > 0 && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold m-0 flex items-center gap-3">
              Screening Hits ({hitTypeFilter === 'all' ? total_hits : `${filteredHits.length} of ${total_hits}`})
              {hitTypeFilter !== 'all' && (
                <button
                  className="px-2 py-0.5 text-xs bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded text-grey-400 cursor-pointer hover:bg-grey-200 dark:hover:bg-grey-700 hover:text-grey-900 dark:hover:text-grey-50"
                  onClick={() => setHitTypeFilter('all')}
                >
                  Show all
                </button>
              )}
            </h3>
            {pending_hits > 0 && (
              <Badge variant="warning">{pending_hits} pending review</Badge>
            )}
          </div>
          <div>
            {filteredHits.map((hit) => (
              <HitCard
                key={hit.id}
                hit={hit}
                onResolve={handleResolveHit}
                isResolving={resolvingHit === hit.id}
              />
            ))}
          </div>
        </div>
      )}

      {total_hits === 0 && (
        <Card className="flex items-center justify-center gap-2 py-6 text-green-500">
          <CheckCircle2 size={24} />
          <span>No screening hits found</span>
        </Card>
      )}
    </div>
  );
}
